import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { toast } from "sonner";
import {
  BookOpen, Play, FileText, Search,
  Stethoscope, Activity, Utensils, AlertTriangle, Smile, Heart, Sparkles,
  MessageSquare, Clock, ChevronDown, ChevronUp, Eye, CheckCircle, Users,
  ArrowLeft, X
} from "lucide-react";

const CATEGORY_CONFIG = {
  BIENVENUE: { icon: Sparkles, color: "bg-emerald-50 text-emerald-600", label: "Bienvenue" },
  TRAITEMENT: { icon: Stethoscope, color: "bg-blue-50 text-blue-600", label: "Traitement" },
  NUTRITION: { icon: Utensils, color: "bg-orange-50 text-orange-600", label: "Nutrition" },
  EFFETS_SECONDAIRES: { icon: Activity, color: "bg-purple-50 text-purple-600", label: "Effets secondaires" },
  SIGNES_ALERTE: { icon: AlertTriangle, color: "bg-red-50 text-red-600", label: "Signes d'alerte" },
  BIEN_VIVRE: { icon: Smile, color: "bg-teal-50 text-teal-600", label: "Bien-être" },
  GESTION_SYMPTOMES: { icon: Heart, color: "bg-pink-50 text-pink-600", label: "Gestion des symptômes" },
};

const TYPE_ICONS = { ARTICLE: FileText, VIDEO: Play };

export default function NurseEducation() {
  const { t } = useLanguage();
  const [contents, setContents] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [activeTab, setActiveTab] = useState("contents");
  const [selectedContent, setSelectedContent] = useState(null);
  const [patientStats, setPatientStats] = useState({});
  const [expandedPatient, setExpandedPatient] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contentsRes, patientsRes] = await Promise.all([
        api.get("/education/professional/contents").catch(() => ({ data: [] })),
        api.get("/patients?limit=500").catch(() => ({ data: [] })),
      ]);
      setContents(contentsRes.data || []);
      const patientList = Array.isArray(patientsRes.data) ? patientsRes.data : patientsRes.data?.items || patientsRes.data?.patients || [];
      setPatients(patientList);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientEducation = useCallback(async (patientId) => {
    if (patientStats[patientId]) {
      setExpandedPatient(expandedPatient === patientId ? null : patientId);
      return;
    }
    try {
      const res = await api.get(`/education/professional/patient/${patientId}/education`);
      setPatientStats(prev => ({ ...prev, [patientId]: res.data }));
      setExpandedPatient(patientId);
    } catch {
      toast.error("Erreur chargement stats patient");
    }
  }, [patientStats, expandedPatient]);

  const filtered = contents.filter(c => {
    const matchSearch = !search || c.title?.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "ALL" || c.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const categories = [...new Set(contents.map(c => c.category).filter(Boolean))];

  if (loading) {
    return (
      <ProfessionalLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
      </ProfessionalLayout>
    );
  }

  // Content detail view
  if (selectedContent) {
    const catConfig = CATEGORY_CONFIG[selectedContent.category] || { icon: BookOpen, color: "bg-slate-50 text-slate-600", label: selectedContent.category };
    const CatIcon = catConfig.icon;
    return (
      <ProfessionalLayout>
      <div className="space-y-6" data-testid="edu-content-detail">
        <button
          onClick={() => setSelectedContent(null)}
          data-testid="edu-back-btn"
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux contenus
        </button>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl ${catConfig.color} flex items-center justify-center`}>
              <CatIcon className="w-5 h-5" />
            </div>
            <div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${catConfig.color}`}>{catConfig.label}</span>
              <h1 className="text-xl font-bold text-slate-800 mt-1">{selectedContent.title}</h1>
            </div>
          </div>

          {selectedContent.shortDescription && (
            <p className="text-slate-600 mb-4 italic">{selectedContent.shortDescription}</p>
          )}

          {selectedContent.keyPoints?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Points clés</h3>
              <ul className="space-y-1">
                {selectedContent.keyPoints.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {selectedContent.youtubeVideoId && (
            <div className="mb-4 rounded-xl overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${selectedContent.youtubeVideoId}`}
                className="w-full aspect-video"
                allowFullScreen
                title={selectedContent.title}
              />
            </div>
          )}

          {selectedContent.bodyText && (
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
              {selectedContent.bodyText}
            </div>
          )}
        </div>
      </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
    <div className="space-y-6" data-testid="nurse-education-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Contenu éducatif</h1>
        <p className="text-sm text-slate-500 mt-1">{contents.length} contenus disponibles</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab("contents")}
          data-testid="edu-tab-contents"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === "contents" ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <BookOpen className="w-4 h-4 inline mr-2" />
          Contenus ({contents.length})
        </button>
        <button
          onClick={() => setActiveTab("patients")}
          data-testid="edu-tab-patients"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === "patients" ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Suivi patients ({patients.length})
        </button>
      </div>

      {/* Contents Tab */}
      {activeTab === "contents" && (
        <>
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="edu-search"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="edu-category-filter"
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="ALL">Toutes les catégories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{CATEGORY_CONFIG[cat]?.label || cat}</option>
              ))}
            </select>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Aucun contenu trouvé</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filtered.map(content => {
                const catConfig = CATEGORY_CONFIG[content.category] || { icon: BookOpen, color: "bg-slate-50 text-slate-600", label: content.category };
                const CatIcon = catConfig.icon;
                const TypeIcon = TYPE_ICONS[content.contentType] || FileText;
                return (
                  <button
                    key={content.id}
                    onClick={() => setSelectedContent(content)}
                    data-testid={`edu-content-${content.id}`}
                    className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow text-left w-full"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl ${catConfig.color} flex items-center justify-center flex-shrink-0`}>
                        <CatIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-400 uppercase">{content.contentType}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${catConfig.color}`}>{catConfig.label}</span>
                        </div>
                        <h3 className="font-semibold text-slate-800 truncate">{content.title}</h3>
                        {content.shortDescription && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{content.shortDescription}</p>
                        )}
                      </div>
                      <Eye className="w-5 h-5 text-slate-300 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Patient Stats Tab */}
      {activeTab === "patients" && (
        <>
          {patients.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Aucun patient</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {patients.map(patient => {
                const pid = patient.id || patient._id;
                const stats = patientStats[pid];
                const isExpanded = expandedPatient === pid;
                return (
                  <div key={pid} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <button
                      onClick={() => fetchPatientEducation(pid)}
                      data-testid={`edu-patient-${pid}`}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-sm">
                          {patient.firstName?.[0]}{patient.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{patient.firstName} {patient.lastName}</p>
                          <p className="text-xs text-slate-400">{patient.pathology || "Patient"}</p>
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </button>

                    {isExpanded && stats && (
                      <div className="px-4 pb-4 border-t border-slate-100">
                        {/* Stats summary */}
                        <div className="grid grid-cols-3 gap-3 mt-3 mb-4">
                          <div className="bg-blue-50 rounded-lg p-3 text-center">
                            <p className="text-lg font-bold text-blue-700">{stats.stats?.totalContentsViewed ?? 0}</p>
                            <p className="text-xs text-blue-600">Contenus vus</p>
                          </div>
                          <div className="bg-emerald-50 rounded-lg p-3 text-center">
                            <p className="text-lg font-bold text-emerald-700">{stats.stats?.totalContentsCompleted ?? 0}</p>
                            <p className="text-xs text-emerald-600">Complétés</p>
                          </div>
                          <div className="bg-amber-50 rounded-lg p-3 text-center">
                            <p className="text-lg font-bold text-amber-700">{stats.questionnaireResponses?.length ?? 0}</p>
                            <p className="text-xs text-amber-600">Quiz répondus</p>
                          </div>
                        </div>

                        {/* Assignments */}
                        {stats.assignments?.length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Contenus assignés</h4>
                            <div className="space-y-2">
                              {stats.assignments.map((a, i) => (
                                <div key={i} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded-lg">
                                  <span className="text-slate-700 truncate flex-1">{a.contentTitle || a.title || "Contenu"}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    a.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                                    a.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                                    "bg-slate-100 text-slate-500"
                                  }`}>
                                    {a.status === "completed" ? "Complété" : a.status === "in_progress" ? "En cours" : "Assigné"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Questionnaire responses */}
                        {stats.questionnaireResponses?.length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Résultats quiz</h4>
                            <div className="space-y-2">
                              {stats.questionnaireResponses.map((r, i) => (
                                <div key={i} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded-lg">
                                  <span className="text-slate-700 truncate flex-1">{r.contentTitle}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-slate-600">{r.score != null ? `${r.score}%` : "-"}</span>
                                    {r.needsFollowUp && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Suivi requis</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Support requests */}
                        {stats.supportRequests?.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Questions du patient</h4>
                            <div className="space-y-2">
                              {stats.supportRequests.map((r, i) => (
                                <div key={i} className="text-sm p-2 bg-amber-50 rounded-lg">
                                  <p className="text-xs text-amber-600 font-medium mb-1">{r.contentTitle}</p>
                                  <p className="text-slate-700">{r.messageText}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {!stats.assignments?.length && !stats.questionnaireResponses?.length && !stats.supportRequests?.length && (
                          <p className="text-sm text-slate-400 text-center py-2">Aucune activité éducative pour ce patient</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
    </ProfessionalLayout>
  );
}
